import http from "./http";

async function fileToDataUrl(file: File): Promise<string> {
    return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
        reader.readAsDataURL(file);
    });
}

async function imageFileToCompressedDataUrl(file: File): Promise<string> {
    const sourceUrl = await fileToDataUrl(file);

    return await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            const maxWidth = 640;
            const maxHeight = 360;
            const widthRatio = maxWidth / image.width;
            const heightRatio = maxHeight / image.height;
            const scale = Math.min(1, widthRatio, heightRatio);
            const targetWidth = Math.max(1, Math.round(image.width * scale));
            const targetHeight = Math.max(1, Math.round(image.height * scale));

            const canvas = document.createElement("canvas");
            canvas.width = targetWidth;
            canvas.height = targetHeight;

            const context = canvas.getContext("2d");
            if (!context) {
                reject(new Error("Canvas context is not available"));
                return;
            }

            context.drawImage(image, 0, 0, targetWidth, targetHeight);
            resolve(canvas.toDataURL("image/jpeg", 0.72));
        };
        image.onerror = () => reject(new Error("Failed to load image for compression"));
        image.src = sourceUrl;
    });
}

export const getTopDestinationConfig = async (search?: string) => {
    const q = search ? `?search=${encodeURIComponent(search)}` : "";
    let results = await http.get(`/utility/admin-top-destination-content${q}`);
    return results.data;
};

export const saveTopDestinationConfig = async (data: any[]) => {
    let results = await http.post(`/utility/admin-top-destination-content`, data);
    return results.data;
};

export const toggleDestinationActive = async (id: string, active: boolean) => {
    return await http.patch(`/utility/admin-destinations/${encodeURIComponent(id)}/active`, { active });
};

export const updateDestinationCoordinates = async (id: string, lat: number, long: number) => {
    return await http.patch(`/utility/admin-destinations/${encodeURIComponent(id)}/coordinates`, { lat, long });
};

export const updateDestinationMeta = async (id: string, patch: Record<string, unknown>) => {
    return await http.patch(`/utility/admin-destinations/${encodeURIComponent(id)}/meta`, patch);
};

export type EnsureFromWorldLibraryPayload = {
    countryCode: string;
    cityName: string;
    lat?: number;
    long?: number;
    timezone?: string;
    timezoneId?: string;
    timezoneHour?: string;
    geonameId?: number;
};

export const ensureDestinationFromWorldLibrary = async (payload: EnsureFromWorldLibraryPayload) => {
    return await http.post(`/utility/admin-destinations/ensure-from-world-library`, payload);
};

export const reorderDestinations = async (orderedIds: string[]) => {
    return await http.post(`/utility/admin-destinations/reorder`, { orderedIds });
};

export const deleteDestination = async (id: string) => {
    return await http.delete(`/utility/admin-destinations/${encodeURIComponent(id)}`);
};

export const uploadDestinationImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("image", file);
    try {
        const { data } = await http.post(`/gcs-upload-single-image?folder=destinations/top`, formData);
        return data?.url || data?.path || "";
    } catch (error) {
        console.warn("GCS upload failed, using compressed data URL fallback", error);
        return await imageFileToCompressedDataUrl(file);
    }
};
